/**
 * Everything an offset-paged query is holding, with nothing said twice.
 *
 * Offset paging assumes the list stands still. Both lists this application
 * pages through -- a row's comments and a row's history -- grow at the *front*:
 * the newest is first, and the next page is asked for by an offset worked out
 * before it arrived. So anything a new entry pushes across a page boundary
 * comes back on both sides of it.
 *
 * Keying the render by id would turn that into a React warning about duplicate
 * keys rather than into a fix. Refetching every page whenever the list changed
 * would be the other fix, and it is a screenful of requests to avoid rendering
 * one row twice.
 */
export function mergedPages<T extends { id: number }>(
  // Structural rather than `InfiniteData<Page, number>`: what `useInfiniteQuery`
  // hands back has its page parameter widened to `unknown`, and the only thing
  // read here is the pages themselves.
  held: { pages: readonly { results: T[] }[] } | undefined,
): T[] {
  return mergedPagesBy(held, (item) => String(item.id))
}

/**
 * The same, for a list whose entries are not identified by an `id`.
 *
 * The activity history is one: its operations are a *grouping* of history
 * entries rather than rows of their own, so what makes two of them the same is
 * the operation they summarise -- and the same operation appears twice under
 * undo, once as it was done and once as it was undone.
 */
export function mergedPagesBy<T>(
  held: { pages: readonly { results: T[] }[] } | undefined,
  identity: (item: T) => string,
): T[] {
  const seen = new Set<string>()
  const all: T[] = []
  for (const page of held?.pages ?? []) {
    for (const item of page.results) {
      const key = identity(item)
      if (seen.has(key)) continue
      seen.add(key)
      all.push(item)
    }
  }
  return all
}
